import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").trim();

export default function Profile() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [plan, setPlan] = useState("");
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [paymentsError, setPaymentsError] = useState("");

  useEffect(() => {
    const fetchProfileAndPayments = async () => {
      const token = localStorage.getItem("jwt_token");

      if (!token || token === "undefined") {
        console.warn("⚠️ No valid token found. Redirecting to login...");
        navigate("/login");
        return;
      }

      try {
        // Fetch profile
        const profileResponse = await fetch(`${BACKEND_URL}/api/clients/profile`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!profileResponse.ok) {
          const text = await profileResponse.text();
          console.error("❌ Profile fetch failed:", profileResponse.status, text);
          throw new Error("Failed to fetch user profile.");
        }

        const profileData = await profileResponse.json();
        setEmail(profileData.email || "");
        setPlan(profileData.plan || "Free");

        // Fetch payments
        const paymentsResponse = await fetch(`${BACKEND_URL}/api/payments/history`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!paymentsResponse.ok) {
          const text = await paymentsResponse.text();
          console.error("❌ Payments fetch failed:", paymentsResponse.status, text);
          setPaymentsError("Failed to load payment history.");
          setPayments([]);
        } else {
          const paymentsData = await paymentsResponse.json();
          console.log("Raw payments data:", paymentsData);

          if (paymentsData.payments && Array.isArray(paymentsData.payments)) {
            setPayments(paymentsData.payments);
            setPaymentsError("");
          } else {
            setPayments([]);
            setPaymentsError("Invalid payment data format.");
          }
        }

        setLoading(false);
      } catch (err) {
        console.error("❌ Error fetching profile or payments:", err);
        setError("Unable to load profile.");
        setLoading(false);
      }
    };

    fetchProfileAndPayments();
  }, [navigate]);

  const handleBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#1a4d42] px-6 py-12">
        <div className="text-slate-300">Loading profile...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#1a4d42] px-6 py-12">
        <div className="bg-[#173a32] border border-red-500/30 rounded-2xl px-6 py-4 text-red-400 font-semibold max-w-md w-full text-center">{error}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f3f4f6] px-6 py-12">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(67,179,126,0.08),_transparent_40%),radial-gradient(circle_at_top_right,_rgba(59,130,246,0.05),_transparent_35%)] pointer-events-none" />
      
      <div className="relative max-w-3xl mx-auto">
        <div className="mb-8 flex justify-between items-center">
          <h1 className="text-3xl font-bold text-slate-900">My Profile</h1>
          <button 
            onClick={handleBack} 
            className="bg-slate-200 hover:bg-slate-300 border border-slate-300 text-slate-900 px-4 py-2 rounded-lg font-semibold transition"
            aria-label="Go back"
          >
            ← Back
          </button>
        </div>

        <div className="grid gap-8">
          {/* Profile Card */}
          <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-lg shadow-black/5">
            <h2 className="text-2xl font-bold text-emerald-700 mb-6 flex items-center gap-2">👤 User Profile</h2>
            
            <div className="space-y-6">
              <div className="flex flex-col gap-2">
                <span className="text-sm uppercase tracking-[0.15em] text-slate-600 font-semibold">Email Address</span>
                <span className="text-lg text-slate-900 font-medium break-all">{email}</span>
              </div>
              
              <div className="flex flex-col gap-2">
                <span className="text-sm uppercase tracking-[0.15em] text-slate-600 font-semibold">Subscription Plan</span>
                <div className="flex items-center gap-3">
                  <span className="text-lg text-emerald-700 font-bold">{plan}</span>
                  {plan !== "Free" && (
                    <span className="inline-flex rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">
                      Active
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Payments Section */}
          <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-lg shadow-black/5">
            <h2 className="text-2xl font-bold text-emerald-700 mb-6 flex items-center gap-2">💳 Payment History</h2>

            {paymentsError ? (
              <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-red-700 font-semibold text-center">
                {paymentsError}
              </div>
            ) : payments.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-slate-500">No payment history available</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Order ID</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Status</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Amount</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Plan</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((p, index) => (
                      <tr key={p.order_id || index} className="border-b border-slate-100 hover:bg-slate-50 transition">
                        <td className="px-4 py-4 text-sm text-slate-700">{p.order_id || "N/A"}</td>
                        <td className="px-4 py-4 text-sm">
                          <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${
                            p.status === "completed" ? "bg-emerald-100 text-emerald-700" :
                            p.status === "pending" ? "bg-amber-100 text-amber-700" :
                            "bg-slate-100 text-slate-700"
                          }`}>
                            {p.status || "N/A"}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-sm font-semibold text-emerald-700">
                          ₹{typeof p.amount === "number" ? p.amount.toFixed(2) : "N/A"}
                        </td>
                        <td className="px-4 py-4 text-sm text-slate-700">{p.plan_name || "N/A"}</td>
                        <td className="px-4 py-4 text-sm text-slate-700">
                          {p.payment_date ? new Date(p.payment_date).toLocaleDateString() : "N/A"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
